import { useMemo, useState } from 'react';
import { useAppStore } from '../../store/appStore';
import { useRegion } from '../../hooks/useRegion';
import { getCategoryColor, ON_CATEGORY_COLOR } from '../../utils/categoryColors';
import { incomeByCategory } from '../../lib/equipmentInsights';

export function CategoryPerformanceCard() {
  const { equipment } = useAppStore();
  const { formatCurrency } = useRegion();
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0 });

  const { incomeData, totalIncome } = useMemo(() => {
    // All equipment, archived too, for lifetime income
    const incomeArr = incomeByCategory(equipment)
      .map((cat) => ({ ...cat, color: getCategoryColor(cat.name) }));
    return {
      incomeData: incomeArr,
      totalIncome: incomeArr.reduce((sum, cat) => sum + cat.value, 0)
    };
  }, [equipment]);

  const hasData = incomeData.length > 0;

  // Find hovered data
  const hoveredData = hoveredCategory 
    ? incomeData.find(c => c.name === hoveredCategory) 
    : null;

  const handleMouseEnter = (cat, e) => {
    setHoveredCategory(cat.name);
    const rect = e.currentTarget.getBoundingClientRect();
    const parentRect = e.currentTarget.parentElement.getBoundingClientRect();
    setTooltipPosition({ x: rect.left - parentRect.left + rect.width / 2 });
  };

  if (!hasData) {
    return (
      <div className="relative h-12 rounded-cards overflow-hidden bg-surface flex items-center justify-center">
        <p className="text-xs text-muted-foreground text-center px-3">
          Income data will appear once equipment starts earning
        </p>
      </div>
    );
  }

  return (
    <div className="relative h-12 rounded-cards">
      {/* Income bar as full background */}
      <div className="absolute inset-0 flex rounded-cards overflow-hidden">
        {incomeData.map((cat) => {
          const percentage = totalIncome > 0 ? (cat.value / totalIncome) * 100 : 0;
          const isHovered = hoveredCategory === cat.name;
          const showPercentage = percentage >= 8; // Show % if segment is wide enough

          return (
            <div
              key={cat.name}
              className={`relative h-full flex-shrink-0 transition-all duration-200 cursor-pointer flex items-center justify-end ${isHovered ? 'brightness-110' : ''}`}
              style={{
                width: `${percentage}%`,
                backgroundColor: cat.color,
                color: ON_CATEGORY_COLOR,
                minWidth: percentage > 0 ? '4px' : '0',
              }}
              onMouseEnter={(e) => handleMouseEnter(cat, e)}
              onMouseLeave={() => setHoveredCategory(null)}
            >
              {showPercentage && (
                <span className="text-xs tabular-nums pr-2">
                  {percentage.toFixed(0)}%
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Title overlay - even padding */}
      <div className="absolute inset-0 flex items-center px-3 pointer-events-none">
        <span className="text-sm italic" style={{ color: ON_CATEGORY_COLOR }}>
          income by category
        </span>
      </div>

      {/* Hover tooltip - positioned above the bar */}
      {hoveredData && (
        <div
          className="absolute bottom-full mb-2 z-50 bg-popover text-popover-foreground rounded-small px-3 py-2 shadow-modal pointer-events-none whitespace-nowrap transform -translate-x-1/2"
          style={{ left: tooltipPosition.x }}
        >
          <p className="text-sm text-center">{hoveredData.name}</p>
          <p className="text-sm text-moss tabular-nums text-center">
            {formatCurrency(hoveredData.value)}
          </p>
          <p className="text-xs text-muted-foreground text-center">
            {hoveredData.count} item{hoveredData.count !== 1 ? 's' : ''}
          </p>
          {/* Arrow pointing down */}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-popover" />
        </div>
      )}
    </div>
  );
}
